import React, { useState, useEffect } from "react";
import "./DynamicFamilyDetailsTable.css";

interface FamilyDetailsEntry {
  relationship: string;
  surname: string;
  firstName: string;
  dateOfBirth: string;
  placeOfBirth: string;
  maritalStatus: string;
  occupation: string;
  fullAddress: string;
  isDeceased: boolean;
  dateOfDeath?: string;
  placeOfDeath?: string;
}

interface DynamicFamilyDetailsTableProps {
  value: { [key: string]: FamilyDetailsEntry };
  onChange: (value: { [key: string]: FamilyDetailsEntry }) => void;
  required?: boolean;
}

const DynamicFamilyDetailsTable: React.FC<DynamicFamilyDetailsTableProps> = ({
  value,
  onChange,
  required = false,
}) => {
  const [entries, setEntries] = useState<{ [key: string]: FamilyDetailsEntry }>(
    value || {}
  );

  useEffect(() => {
    setEntries(value || {});
  }, [value]);

  // Add keyboard support for Ctrl+Enter to add new entry
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.ctrlKey && event.key === "Enter") {
        event.preventDefault();
        addEntry();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  const addEntry = () => {
    const entryCount = Object.keys(entries).length;
    const newKey = `familyDetails${entryCount + 1}`;

    const newEntry: FamilyDetailsEntry = {
      relationship: "",
      surname: "",
      firstName: "",
      dateOfBirth: "",
      placeOfBirth: "",
      maritalStatus: "",
      occupation: "",
      fullAddress: "",
      isDeceased: false,
      dateOfDeath: "",
      placeOfDeath: "",
    };

    const updatedEntries = { ...entries, [newKey]: newEntry };
    setEntries(updatedEntries);
    onChange(updatedEntries);
  };

  const removeEntry = (key: string) => {
    const { [key]: removed, ...remainingEntries } = entries;

    // Renumber the remaining entries
    const renumberedEntries: { [key: string]: FamilyDetailsEntry } = {};
    const remainingKeys = Object.keys(remainingEntries).sort();

    remainingKeys.forEach((oldKey, index) => {
      const newKey = `familyDetails${index + 1}`;
      renumberedEntries[newKey] = remainingEntries[oldKey];
    });

    setEntries(renumberedEntries);
    onChange(renumberedEntries);
  };

  const updateEntry = (
    key: string,
    field: keyof FamilyDetailsEntry,
    value: string | boolean
  ) => {
    const currentEntry = entries[key];
    if (!currentEntry) return;

    const updatedEntries = {
      ...entries,
      [key]: { ...currentEntry, [field]: value },
    };

    setEntries(updatedEntries);
    onChange(updatedEntries);
  };

  // Utility function to transform flat data to nested structure
  const transformToNestedStructure = (flatData: {
    [key: string]: FamilyDetailsEntry;
  }) => {
    const nested: { [key: string]: any } = {};

    Object.entries(flatData).forEach(([key, entry]) => {
      const relationship = entry.relationship;

      if (relationship === "Father") {
        nested.parent1 = entry;
      } else if (relationship === "Mother") {
        nested.parent2 = entry;
      } else if (relationship === "Son" || relationship === "Daughter") {
        const childCount = Object.keys(nested).filter((k) =>
          k.startsWith("child")
        ).length;
        nested[`child${childCount + 1}`] = entry;
      } else if (relationship === "Brother" || relationship === "Sister") {
        const siblingCount = Object.keys(nested).filter((k) =>
          k.startsWith("sibling")
        ).length;
        nested[`sibling${siblingCount + 1}`] = entry;
      } else if (relationship === "Grandfather") {
        const grandparentCount = Object.keys(nested).filter((k) =>
          k.startsWith("grandparent")
        ).length;
        nested[`grandparent${grandparentCount + 1}`] = entry;
      } else if (relationship === "Grandmother") {
        const grandparentCount = Object.keys(nested).filter((k) =>
          k.startsWith("grandparent")
        ).length;
        nested[`grandparent${grandparentCount + 1}`] = entry;
      } else if (relationship === "Uncle") {
        const uncleCount = Object.keys(nested).filter((k) =>
          k.startsWith("uncle")
        ).length;
        nested[`uncle${uncleCount + 1}`] = entry;
      } else if (relationship === "Aunt") {
        const auntCount = Object.keys(nested).filter((k) =>
          k.startsWith("aunt")
        ).length;
        nested[`aunt${auntCount + 1}`] = entry;
      } else if (relationship === "Wife" || relationship === "Husband") {
        const spouseCount = Object.keys(nested).filter((k) =>
          k.startsWith("spouse")
        ).length;
        nested[`spouse${spouseCount + 1}`] = entry;
      } else {
        const otherCount = Object.keys(nested).filter((k) =>
          k.startsWith("other")
        ).length;
        nested[`other${otherCount + 1}`] = entry;
      }
    });

    return nested;
  };

  const entryKeys = Object.keys(entries).sort();

  return (
    <div className="dynamic-family-details-table">
      <div className="table-header">
        <h4>Provide Details of All Family Members Including Deceased</h4>
        <div className="header-actions">
          <button
            type="button"
            className="add-row-btn"
            onClick={addEntry}
            title="Add new family member entry (Ctrl+Enter)"
          >
            + Add Family Member
          </button>
        </div>
      </div>

      {entryKeys.length === 0 ? (
        <div className="empty-state">
          <p>No family members added yet.</p>
          <button type="button" className="add-first-btn" onClick={addEntry}>
            Add First Family Member
          </button>
        </div>
      ) : (
        <div className="entries-container">
          {entryKeys.map((key, index) => (
            <div key={key} className="entry-card">
              <div className="entry-header">
                <div className="entry-number">
                  <span className="entry-badge">{index + 1}</span>
                  <h5>Entry {index + 1}</h5>
                </div>
                <button
                  type="button"
                  className="remove-btn"
                  onClick={() => removeEntry(key)}
                  title="Remove this family member entry"
                >
                  ×
                </button>
              </div>
              
              <div className="entry-form">
                <div className="form-row form-row-1">
                  <div className="field-group">
                    <label>Relationship</label>
                    <select
                      value={entries[key]?.relationship}
                      onChange={(e) =>
                        updateEntry(key, "relationship", e.target.value)
                      }
                    >
                      <option value="">Select relationship</option>
                      <option value="Father">Father</option>
                      <option value="Mother">Mother</option>
                      <option value="Brother">Brother</option>
                      <option value="Sister">Sister</option>
                      <option value="Son">Son</option>
                      <option value="Daughter">Daughter</option>
                      <option value="Wife">Wife</option>
                      <option value="Husband">Husband</option>
                      <option value="Grandfather">Grandfather</option>
                      <option value="Grandmother">Grandmother</option>
                      <option value="Uncle">Uncle</option>
                      <option value="Aunt">Aunt</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                  
                  <div className="field-group">
                    <label>Surname</label>
                    <input
                      type="text"
                      value={entries[key]?.surname || ""}
                      onChange={(e) =>
                        updateEntry(key, "surname", e.target.value)
                      }
                      placeholder="e.g., Smith"
                    />
                  </div>
                  
                  <div className="field-group">
                    <label>First Name</label>
                    <input
                      type="text"
                      value={entries[key]?.firstName || ""}
                      onChange={(e) =>
                        updateEntry(key, "firstName", e.target.value)
                      }
                      placeholder="e.g., John"
                    />
                  </div>
                </div>
                
                <div className="form-row form-row-2">
                  <div className="field-group">
                    <label>Date of Birth</label>
                    <input
                      type="text"
                      value={entries[key]?.dateOfBirth || ""}
                      onChange={(e) =>
                        updateEntry(key, "dateOfBirth", e.target.value)
                      }
                      placeholder="yyyy-mm-dd"
                    />
                  </div>
                  
                  <div className="field-group">
                    <label>Place of Birth</label>
                    <input
                      type="text"
                      value={entries[key]?.placeOfBirth || ""}
                      onChange={(e) =>
                        updateEntry(key, "placeOfBirth", e.target.value)
                      }
                      placeholder="e.g., Toronto, Canada"
                    />
                  </div>
                  
                  <div className="field-group">
                    <label>Marital Status</label>
                    <select
                      value={entries[key]?.maritalStatus || ""}
                      onChange={(e) =>
                        updateEntry(key, "maritalStatus", e.target.value)
                      }
                    >
                      <option value="">Select status</option>
                      <option value="S">Single (S)</option>
                      <option value="M">Married (M)</option>
                      <option value="W">Widow/Widower (W)</option>
                      <option value="D">Divorced (D)</option>
                    </select>
                  </div>
                </div>
                
                <div className="form-row form-row-3">
                  <div className="field-group">
                    <label>Occupation</label>
                    <input
                      type="text"
                      value={entries[key]?.occupation || ""}
                      onChange={(e) =>
                        updateEntry(key, "occupation", e.target.value)
                      }
                      placeholder="e.g., Engineer"
                    />
                  </div>
                  
                  <div className="field-group field-group-wide">
                    <label>Full Address</label>
                    <textarea
                      value={entries[key]?.fullAddress || ""}
                      onChange={(e) =>
                        updateEntry(key, "fullAddress", e.target.value)
                      }
                      placeholder="Full address with country, state and pin code"
                      rows={2}
                    />
                  </div>
                </div>
                
                <div className="form-row form-row-4">
                  <div className="field-group deceased-field">
                    <label>Status (Check if Deceased)</label>
                    <div className="deceased-section">
                      <label className="deceased-checkbox">
                        <input
                          type="checkbox"
                          checked={entries[key]?.isDeceased || false}
                          onChange={(e) =>
                            updateEntry(key, "isDeceased", e.target.checked)
                          }
                        />
                      </label>
                      {entries[key]?.isDeceased && (
                        <div className="death-details">
                          <input
                            type="text"
                            value={entries[key]?.dateOfDeath || ""}
                            onChange={(e) =>
                              updateEntry(key, "dateOfDeath", e.target.value)
                            }
                            placeholder="Date of death"
                            className="death-date"
                          />
                          <input
                            type="text"
                            value={entries[key]?.placeOfDeath || ""}
                            onChange={(e) =>
                              updateEntry(key, "placeOfDeath", e.target.value)
                            }
                            placeholder="Place of death"
                            className="death-place"
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}

          {/* Add another button at the bottom */}
          <div className="table-footer">
            <p className="add-hint">Need to add more family members?</p>
            <button
              type="button"
              className="add-another-btn"
              onClick={addEntry}
            >
              + Add Another Family Member
            </button>
          </div>
        </div>
      )}

      {required && entryKeys.length === 0 && (
        <small className="error-text">
          At least one family member entry is required
        </small>
      )}
    </div>
  );
};

export default DynamicFamilyDetailsTable;
